import React, { useCallback, useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import qs from "query-string";

import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import useToggle from "react-use/lib/useToggle";
import { useTranslation } from "react-i18next";

import { useMutation, useQuery } from "@apollo/client";
import { useRouteMatch, useHistory, useLocation, Link } from "react-router-dom";

import { QUERY_ACTIVE_DIRECTORIES } from "../../../../config/graphql/query";
import { MUTATION_DELETE_ACTIVE_DIRECTORY_INTEGRATION } from "../../../../config/graphql/mutation";

import Table from "../../../../components/Table";
import ConnectAppOnlyModal from "./ConnectAppOnlyModal";
import SyncStatus, { useSyncPolling } from "./SyncStatus";

import { readConsentDraft, IActiveDirectoryConsentDraft } from "./consentDraft";

import { useClientId } from "../hooks/useClient";

import { getApiUrl } from "../../../../lib/common/getApiUrl";

const TableRow = ({
  item,
  clientId,
}: {
  item: IActiveDirectory;
  clientId: string;
}) => {
  const { t } = useTranslation(["client", "common"]);

  const { url } = useRouteMatch();

  const { id, name, email, title, createdAt, authMode, tenantId } = item;

  // Older integrations carry no value = delegated.
  const isAppOnly = authMode === "appOnly";

  const [visible, setVisible] = useToggle(false);

  const [onDelete] = useMutation(MUTATION_DELETE_ACTIVE_DIRECTORY_INTEGRATION, {
    // By name, so the list refetches with its own ClientId header.
    refetchQueries: ["activeDirectories"],
    context: { headers: { ClientId: clientId } },
    variables: { id },
  });

  const onBeforeDelete = () => {
    onDelete();

    return setVisible(false);
  };

  return (
    <>
      <tr>
        <td>{title || name || "—"}</td>
        <td>
          {email}
          {isAppOnly && tenantId && (
            <small className="d-block text-muted">{tenantId}</small>
          )}
        </td>
        <td>
          <span
            className={`badge badge-${isAppOnly ? "success" : "secondary"}`}
          >
            {t(
              `client:client.integrations.activeDirectory.mode.${
                isAppOnly ? "appOnly" : "delegated"
              }`,
            )}
          </span>
        </td>
        <td>
          <SyncStatus item={item} />
        </td>
        <td>{createdAt ? dayjs(createdAt).format("L LT") : "—"}</td>
        <td>
          <div className="d-flex">
            <Button
              size="sm"
              variant="danger"
              className="mr-2"
              onClick={() => setVisible(true)}
            >
              {t("common:delete")}
            </Button>
            <Link to={`${url}/${id}`}>
              <Button size="sm" variant="primary">
                {t("common:view")}
              </Button>
            </Link>
          </div>
        </td>
      </tr>
      <Modal
        size="sm"
        show={visible}
        onHide={setVisible}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("client:client.integrations.activeDirectory.modal.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.integrations.activeDirectory.modal.body", {
            id,
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setVisible(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const ClientIntegrationRoute = React.memo(() => {
  const clientId = useClientId();

  const { t } = useTranslation(["client", "common"]);

  const [connectVisible, setConnectVisible] = useToggle(false);

  const history = useHistory();
  const location = useLocation();

  const [consentReturn, setConsentReturn] =
    useState<IActiveDirectoryConsentDraft | null>(null);
  const [consentGranted, setConsentGranted] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);

  // Back from Microsoft on a fresh page: the outcome is in the URL, the form in the draft.
  useEffect(() => {
    const params = qs.parse(location.search);

    const granted = params.activeDirectoryAdminConsent === "granted";
    const refused = typeof params.error === "string" ? params.error : null;
    const draft = readConsentDraft(clientId as string);

    if (!granted && !(refused && draft)) {
      return;
    }

    setConsentReturn({
      tenantId: (params.tenantId as string) || draft?.tenantId,
      email: draft?.email,
      title: draft?.title,
    });
    setConsentGranted(granted);
    setConsentError(refused);
    setConnectVisible(true);

    history.replace(location.pathname);
  }, [
    location.search,
    location.pathname,
    clientId,
    history,
    setConnectVisible,
  ]);

  const { data, startPolling, stopPolling } = useQuery<{
    activeDirectories: IActiveDirectory[];
  }>(QUERY_ACTIVE_DIRECTORIES, {
    fetchPolicy: "network-only",
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const items = data?.activeDirectories ?? [];

  useSyncPolling(
    items.some(({ syncRunning }) => syncRunning),
    { startPolling, stopPolling },
  );

  const renderItem = useCallback(
    (item: IActiveDirectory) => (
      <TableRow key={item.id} item={item} clientId={clientId as string} />
    ),
    [clientId],
  );

  const apiUrl = getApiUrl();

  const integrationOAuthURL = useMemo(
    () =>
      qs.stringifyUrl(
        {
          url: `${apiUrl}/api/v1/activedirectory/auth/login`,
          query: {
            client: clientId,
            redirectUri: window.location.href,
          },
        },
        { encode: true },
      ),
    [clientId, apiUrl],
  );

  return (
    <div className="container-fluid">
      {/* Same two cards as Outlook: each carries its own name, consequence and
          button. `d-flex` on the column plus `mt-auto` on the button keeps both
          buttons on one line however long the texts are in a given language. */}
      <div className="row my-4">
        <div className="col-md-6 d-flex mb-3 mb-md-0">
          <div className="card w-100">
            <div className="card-body d-flex flex-column">
              <h6 className="card-title mb-2">
                {t(
                  "client:client.integrations.activeDirectory.help.appOnlyTitle",
                )}
              </h6>
              <small className="text-muted">
                {t(
                  "client:client.integrations.activeDirectory.help.appOnlyText",
                )}
              </small>
              <small className="d-block mt-3">
                <strong>
                  {t(
                    "client:client.integrations.activeDirectory.help.appOnlyNeeds",
                  )}
                </strong>
              </small>
              <div className="mt-auto pt-3">
                <Button
                  variant="primary"
                  onClick={() => setConnectVisible(true)}
                >
                  {t(
                    "client:client.integrations.activeDirectory.button.connectAppOnly",
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6 d-flex">
          <div className="card w-100">
            <div className="card-body d-flex flex-column">
              <h6 className="card-title mb-2">
                {t(
                  "client:client.integrations.activeDirectory.help.delegatedTitle",
                )}
              </h6>
              <small className="text-muted">
                {t(
                  "client:client.integrations.activeDirectory.help.delegatedText",
                )}{" "}
                <span className="text-danger">
                  {t(
                    "client:client.integrations.activeDirectory.help.delegatedWarning",
                  )}
                </span>
              </small>
              <small className="d-block mt-3">
                <strong>
                  {t(
                    "client:client.integrations.activeDirectory.help.delegatedNeeds",
                  )}
                </strong>
              </small>
              <div className="mt-auto pt-3">
                <a
                  href={integrationOAuthURL}
                  className="btn btn-outline-primary"
                >
                  {t(
                    "client:client.integrations.activeDirectory.button.generate",
                  )}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConnectAppOnlyModal
        clientId={clientId as string}
        visible={connectVisible}
        onHide={() => setConnectVisible(false)}
        initial={consentReturn}
        consentGranted={consentGranted}
        consentError={consentError}
      />
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th scope="col">
              {t("client:client.integrations.activeDirectory.th.name")}
            </th>
            <th scope="col">
              {t("client:client.integrations.activeDirectory.th.email")}
            </th>
            <th scope="col">
              {t("client:client.integrations.activeDirectory.th.mode")}
            </th>
            <th scope="col">
              {t("client:client.integrations.activeDirectory.th.lastSync")}
            </th>
            <th scope="col">
              {t("client:client.integrations.activeDirectory.th.created")}
            </th>
            <th scope="col">
              {t("client:client.integrations.activeDirectory.th.actions")}
            </th>
          </tr>
        </thead>
        <tbody>{items.map(renderItem)}</tbody>
      </Table>
    </div>
  );
});

export default ClientIntegrationRoute;
